import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Building2 } from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";

export default function LocalOrgsPage() {
  const [orgs, setOrgs] = useState([]);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  usePageTitle("Local Organizations");

  useEffect(() => {
    Promise.all([
      api.get("/content/local_org_details"),
      api.get("/pages/by-slug/local-organizations").catch(() => ({ data: null })),
    ]).then(([o, p]) => {
      setOrgs(o.data);
      setPage(p.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <div data-testid="local-orgs-page">
      {/* Hero */}
      <div className="bg-jci-black relative overflow-hidden">
        {page?.image_url ? (
          <div className="relative h-[300px] md:h-[400px]">
            <img src={page.image_url} alt="Local Organizations" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-jci-black/60" />
            <div className="absolute inset-0 flex items-center">
              <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">Local Organizations</h1>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">Local Organizations</h1>
            </div>
          </div>
        )}
        <div className="h-1 bg-gradient-to-r from-jci-blue via-jci-teal to-jci-navy" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-16">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
            {[1,2,3,4,5,6,7,8,9,10].map(i => <Skeleton key={i} className="aspect-square rounded-2xl" />)}
          </div>
        ) : orgs.length === 0 ? (
          <div className="text-center py-20">
            <Building2 className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <p className="text-slate-400">Local organizations will be added soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6" data-testid="local-orgs-grid">
            {orgs.map((org, i) => (
              <Link to={`/local-org/${org.slug}`} key={org.id} data-testid={`local-org-card-${i}`}>
                <Card className="card-hover border-0 shadow-md overflow-hidden aspect-square group">
                  <img
                    src={org.logo_url || "https://via.placeholder.com/400x400?text=Logo"}
                    alt={org.name}
                    className="w-full h-full object-contain p-4 transition-transform duration-300 group-hover:scale-105"
                  />
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
